import { create } from 'zustand';
import NetInfo, { NetInfoState } from '@react-native-community/netinfo';
import axios from 'axios';
import { Platform } from 'react-native';
import { queryClient } from '../api/queryClient';

export type ConnectivityState = 'online' | 'offline' | 'server-down';

const HEALTH_CHECK_INTERVAL_MS = 30_000;
const HEALTH_CHECK_TIMEOUT_MS = 5_000;

/**
 * Resolve the health endpoint the same way the API client resolves its base URL
 * (see src/api/client.ts): relative on web, EXPO_PUBLIC_API_URL on native.
 *
 * These must stay in agreement — if this one kept using an absolute URL while
 * the client went relative, the connectivity banner would report 'server-down'
 * against a perfectly healthy app.
 */
const getHealthUrl = (): string => {
  const envUrl = process.env.EXPO_PUBLIC_API_URL;
  if (envUrl) return `${envUrl}/health`;

  if (Platform.OS === 'web') return '/health';

  return Platform.select({
    android: 'http://10.0.2.2:8000/health',
    default: 'http://localhost:8000/health',
  })!;
};

const HEALTH_URL = getHealthUrl();

interface NetworkState {
  isDeviceOnline: boolean;
  isApiReachable: boolean;
  lastApiReachableAt: Date | null;
  connectivityState: ConnectivityState;

  /** Call once on app startup to subscribe to NetInfo + start health polling. */
  initialize: () => () => void;

  /** Called by the API client when a request fails with a transient error. */
  markApiUnreachable: () => void;

  /** Called by the API client on any successful response. */
  markApiReachable: () => void;
}

function deriveConnectivityState(
  isDeviceOnline: boolean,
  isApiReachable: boolean,
): ConnectivityState {
  if (!isDeviceOnline) return 'offline';
  if (!isApiReachable) return 'server-down';
  return 'online';
}

export const useNetworkStore = create<NetworkState>((set, get) => ({
  isDeviceOnline: true,
  isApiReachable: true,
  lastApiReachableAt: null,
  connectivityState: 'online',

  initialize: () => {
    // Subscribe to device connectivity changes
    const unsubscribe = NetInfo.addEventListener((state: NetInfoState) => {
      const isDeviceOnline = !!(state.isConnected && state.isInternetReachable !== false);
      const { isApiReachable } = get();
      set({
        isDeviceOnline,
        connectivityState: deriveConnectivityState(isDeviceOnline, isApiReachable),
      });

      // When coming back online, immediately check API health
      if (isDeviceOnline && !isApiReachable) {
        checkHealth();
      }
    });

    // Periodic health check
    const checkHealth = async () => {
      const { isDeviceOnline } = get();
      if (!isDeviceOnline) return;

      try {
        await axios.get(HEALTH_URL, { timeout: HEALTH_CHECK_TIMEOUT_MS });
        const wasDown = !get().isApiReachable;
        const now = new Date();
        set({
          isApiReachable: true,
          lastApiReachableAt: now,
          connectivityState: deriveConnectivityState(get().isDeviceOnline, true),
        });
        // Recovering from offline/server-down: mark mounted queries stale so
        // TanStack Query refetches the screens the user is actually looking
        // at. Limiting to `refetchType: 'active'` avoids a request storm that
        // could otherwise hammer the backend right after it comes back.
        if (wasDown) {
          queryClient.invalidateQueries({ refetchType: 'active' });
        }
      } catch {
        set({
          isApiReachable: false,
          connectivityState: deriveConnectivityState(get().isDeviceOnline, false),
        });
      }
    };

    // Run initial health check
    checkHealth();

    const intervalId = setInterval(checkHealth, HEALTH_CHECK_INTERVAL_MS);

    // Return cleanup function
    return () => {
      unsubscribe();
      clearInterval(intervalId);
    };
  },

  markApiUnreachable: () => {
    const { isDeviceOnline } = get();
    set({
      isApiReachable: false,
      connectivityState: deriveConnectivityState(isDeviceOnline, false),
    });
  },

  markApiReachable: () => {
    const wasDown = !get().isApiReachable;
    const { isDeviceOnline } = get();
    set({
      isApiReachable: true,
      lastApiReachableAt: new Date(),
      connectivityState: deriveConnectivityState(isDeviceOnline, true),
    });
    // Recovering from down state: refetch only queries that are currently
    // mounted (active) so we don't fire a request storm across every
    // cached query on recovery.
    if (wasDown) {
      queryClient.invalidateQueries({ refetchType: 'active' });
    }
  },
}));
